import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Tab,
  TabList,
  TabValue,
  makeStyles,
  tokens,
} from "@fluentui/react-components";

import { FilterState, Room } from "../../types";
import { useAppointmentDetails, useRoomSearch, useEquipment, useRoomBooking, useOfficeFilter } from "../hooks";
import { isCrossDomain } from "../../utils";
import { OutlookCalendarService } from "../../services/OutlookCalendarService";
import { clearAuthCache, getDeskareAccessToken, signOut as officeSignOut } from "../../auth/officeAuth";
import { UI_TIMERS } from "../../constants";
import { ERROR_CONTEXT_MESSAGES, UI_LABELS } from "../../constants/messages";
import { getTranslations } from "../../i18n";
import { AppError, ErrorCode } from "../../errors/AppError";
import { SignOutRegular, PersonRegular } from "@fluentui/react-icons";

const BookRoomTab = React.lazy(() =>
  import("./BookRoomTab").then((m) => ({ default: m.BookRoomTab }))
);
const ReservationsTab = React.lazy(() =>
  import("./ReservationsTab").then((m) => ({ default: m.ReservationsTab }))
);
const BookingConfirmationDialog = React.lazy(() =>
  import("./BookingConfirmationDialog").then((m) => ({ default: m.BookingConfirmationDialog }))
);
const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  content: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingHorizontalM,
  },
  messageBar: {
    margin: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  tabRow: {
    marginBottom: tokens.spacingVerticalM,
  },
  tabsInline: {
    display: "flex",
    width: "100%",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    padding: "2px",
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    padding: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    marginTop: "auto",
  },
  signedOutContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  tabContent: {
    marginTop: tokens.spacingVerticalS,
  },
  roomsLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(280px, 360px) 1fr",
    gap: tokens.spacingHorizontalXL,
    alignItems: "start",
    marginTop: tokens.spacingVerticalM,
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  surface: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow4,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  toolbar: {
    marginTop: tokens.spacingVerticalM,
  },
  insightsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalM,
  },
  statRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  statValue: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  quickRoomList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  personaStack: {
    display: "grid",
    gap: tokens.spacingVerticalS,
  },
  compactCard: {
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  emptyState: {
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
});

const App: React.FC = () => {
  const styles = useStyles();

  const {
    details: appointmentDetails,
    loading: appointmentLoading,
    error: appointmentError,
  } = useAppointmentDetails();

  const {
    rooms,
    loading: roomsLoading,
    error: roomsError,
    searchRooms,
    clearError: clearRoomsError,
  } = useRoomSearch();

  const { equipment: equipmentOptions } = useEquipment();

  const {
    bookingInProgress,
    bookingError,
    confirmation,
    bookRoom,
    bookRoomViaDeskare,
    dismissError: clearBookingError,
    dismissConfirmation,
  } = useRoomBooking();

  const [filters, setFilters] = useState<FilterState>({
    minCapacity: 1,
    selectedEquipment: [],
  });

  const officeFilter = useOfficeFilter(appointmentDetails, filters, searchRooms);

  const dismissError = () => {
    clearRoomsError();
    clearBookingError();
  };
  const [activeTab, setActiveTab] = useState<TabValue>("book");
  const [signedOut, setSignedOut] = useState(false);
  const capacityManuallyModifiedRef = useRef(false);
  const initialCapacitySetRef = useRef(false);
  const appointmentDetailsRef = useRef(appointmentDetails);
  const searchRoomsRef = useRef(searchRooms);
  const selectedOfficeIdRef = useRef(officeFilter.selectedOfficeId);

  // Keep refs up to date
  useEffect(() => {
    appointmentDetailsRef.current = appointmentDetails;
    searchRoomsRef.current = searchRooms;
    selectedOfficeIdRef.current = officeFilter.selectedOfficeId;
  }, [appointmentDetails, searchRooms, officeFilter.selectedOfficeId]);

  // Initialize filters only once when appointmentDetails is first loaded
  useEffect(() => {
    if (!appointmentDetails || initialCapacitySetRef.current) return;

    const attendeeCount = OutlookCalendarService.getAttendeeCount(appointmentDetails);
    const initialFilters: FilterState = {
      minCapacity: Math.max(1, attendeeCount),
      selectedEquipment: [], // Reset equipment on initial load only
    };

    initialCapacitySetRef.current = true;
    setFilters(initialFilters);
    void searchRooms(appointmentDetails, initialFilters, selectedOfficeIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentDetails]);

  // Update capacity automatically only if user hasn't modified it manually
  // and only when attendee count changes
  useEffect(() => {
    if (!appointmentDetails || !initialCapacitySetRef.current || capacityManuallyModifiedRef.current) {
      return;
    }

    const attendeeCount = OutlookCalendarService.getAttendeeCount(appointmentDetails);
    const newCapacity = Math.max(1, attendeeCount);

    setFilters((prev) => {
      if (prev.minCapacity !== newCapacity) {
        return { ...prev, minCapacity: newCapacity };
      }
      return prev;
    });
  }, [
    appointmentDetails?.requiredAttendees.length,
    appointmentDetails?.optionalAttendees.length,
  ]);

  useEffect(() => {
    if (!bookingError) return undefined;
    if (bookingError.type !== "conflict" && bookingError.type !== "validation") return undefined;

    const timer = setTimeout(dismissError, UI_TIMERS.ERROR_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [bookingError, dismissError]);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    // Track if user manually modified capacity
    setFilters((prevFilters) => {
      if (newFilters.minCapacity !== prevFilters.minCapacity) {
        capacityManuallyModifiedRef.current = true;
      }
      return newFilters;
    });
    // Auto-search will be triggered by CollapsibleFilters component
    if (appointmentDetailsRef.current) {
      void searchRoomsRef.current(appointmentDetailsRef.current, newFilters, selectedOfficeIdRef.current);
    }
  }, []);

  const handleRoomBook = (room: Room) => {
    if (!appointmentDetails) return;

    if (!room.email || isCrossDomain(appointmentDetails.organizer, room.email)) {
      void bookRoomViaDeskare(room, appointmentDetails);
      return;
    }

    void bookRoom(room, appointmentDetails);
  };

  const handleSignOut = useCallback(() => {
    void (async () => {
      await officeSignOut();
      setSignedOut(true);
    })();
  }, []);

  const handleSignIn = useCallback(() => {
    window.location.reload();
  }, []);

  const handleSearchOtherRooms = () => {
    dismissError();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReAuthenticate = () => {
    void (async () => {
      clearAuthCache();
      try {
        await getDeskareAccessToken({ forceRefresh: true });
      } catch {
        dismissError();
        return;
      }
      // Full reload ensures every hook and API call restarts with the fresh token.
      window.location.reload();
    })();
  };

  const getAccountNotFoundMessage = (): string => {
    const userEmail =
      (typeof Office !== "undefined" && Office.context?.mailbox?.userProfile?.emailAddress) ||
      appointmentDetails?.organizer ||
      "";
    const template = getTranslations().errors.accountNotFound;
    return template.replace("{email}", userEmail || "—");
  };

  const getErrorMessage = (): string | null => {
    const error = roomsError || bookingError;
    if (!error) return null;

    if (error instanceof AppError && error.code === ErrorCode.ACCOUNT_NOT_FOUND) {
      return getAccountNotFoundMessage();
    }

    const contextMessage = ERROR_CONTEXT_MESSAGES[error.type] || "";
    return error.message + contextMessage;
  };

  const handleChangeRoom = () => {
    // Switch to book tab when user wants to change room
    setActiveTab("book");
  };

  if (appointmentLoading) {
    return (
      <div className={styles.root}>
        <div className={styles.loading}>
          <Spinner size="large" label={UI_LABELS.LOADING_APPOINTMENT} />
        </div>
      </div>
    );
  }

  if (appointmentError || !appointmentDetails) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <MessageBar intent="error" className={styles.messageBar}>
            <MessageBarBody>
              {appointmentError || "Failed to load appointment details"}
            </MessageBarBody>
          </MessageBar>
        </div>
      </div>
    );
  }

  if (signedOut) {
    return (
      <div className={styles.root}>
        <div className={styles.signedOutContainer}>
          <PersonRegular fontSize={48} />
          <span>{UI_LABELS.SIGNED_OUT_MESSAGE}</span>
          <Button appearance="primary" icon={<PersonRegular />} onClick={handleSignIn}>
            {UI_LABELS.SIGN_IN}
          </Button>
        </div>
      </div>
    );
  }

  const displayError = roomsError || bookingError;
  const isAccountNotFound =
    displayError && AppError.isCode(displayError, ErrorCode.ACCOUNT_NOT_FOUND);

  if (isAccountNotFound) {
    return (
      <div className={styles.root}>
        <div className={styles.content}>
          <MessageBar intent="error" className={styles.messageBar}>
            <MessageBarBody>{getAccountNotFoundMessage()}</MessageBarBody>
            <MessageBarActions>
              <Button appearance="subtle" size="small" onClick={dismissError}>
                {UI_LABELS.CLOSE}
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <React.Suspense fallback={<Spinner />}>
          {displayError && (
            <MessageBar intent="error" className={styles.messageBar}>
              <MessageBarBody>{getErrorMessage()}</MessageBarBody>
              <MessageBarActions>
                {bookingError?.type === "conflict" && (
                  <Button appearance="primary" size="small" onClick={handleSearchOtherRooms}>
                    {UI_LABELS.VIEW_OTHER_ROOMS}
                  </Button>
                )}
                {(roomsError?.type === "auth" ||
                  bookingError?.type === "auth" ||
                  roomsError?.type === "network" ||
                  bookingError?.type === "network") &&
                  !AppError.isCode(roomsError, ErrorCode.ACCOUNT_NOT_FOUND) &&
                  !AppError.isCode(bookingError, ErrorCode.ACCOUNT_NOT_FOUND) && (
                  <Button appearance="primary" size="small" onClick={handleReAuthenticate}>
                    {UI_LABELS.RECONNECT}
                  </Button>
                )}
                <Button appearance="subtle" size="small" onClick={dismissError}>
                  {UI_LABELS.CLOSE}
                </Button>
              </MessageBarActions>
            </MessageBar>
          )}

          <div className={styles.tabRow}>
            <TabList
              selectedValue={activeTab}
              onTabSelect={(_, data) => setActiveTab(data.value)}
              className={styles.tabsInline}
              size="medium"
              appearance="subtle"
            >
              <Tab value="book">
                {UI_LABELS.TABS_BOOK}
              </Tab>
              <Tab value="reservations">
                {UI_LABELS.TABS_RESERVATIONS}
              </Tab>
            </TabList>
          </div>

          <div className={styles.tabContent}>
            {activeTab === "book" && (
              <BookRoomTab
                appointmentDetails={appointmentDetails}
                filters={filters}
                rooms={rooms}
                equipmentOptions={equipmentOptions}
                roomsLoading={roomsLoading}
                bookingInProgress={bookingInProgress}
                onFilterChange={handleFilterChange}
                onRoomBook={handleRoomBook}
                officeFilter={officeFilter}
              />
            )}
            {activeTab === "reservations" && (
              <ReservationsTab onChangeRoom={handleChangeRoom} />
            )}
          </div>
        </React.Suspense>
      </div>

      <div className={styles.footer}>
        <Button
          appearance="transparent"
          size="small"
          icon={<SignOutRegular />}
          onClick={handleSignOut}
        >
          {UI_LABELS.SIGN_OUT}
        </Button>
      </div>

      <React.Suspense fallback={null}>
        <BookingConfirmationDialog
          isOpen={confirmation !== null}
          onDismiss={dismissConfirmation}
          onBookAnother={dismissConfirmation}
          booking={confirmation}
        />
      </React.Suspense>
    </div>
  );
};

export default App;
